import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { T } from '@design/index';
import { onInsuranceClaimCreated, formatPrice } from './bookingUtils';
import { useStore } from '../../state/patientProductionStore';
import { patientAuthFlow } from '../../auth/patientAuthFlow';
import { patientApi } from '../../api';
import type { InsurancePolicy, InsuranceClaim } from '../../types';

const card = (extra?: React.CSSProperties): React.CSSProperties => ({
  background: T.white, border: `1px solid ${T.border}`, borderRadius: 14,
  padding: 16, boxShadow: T.shadow, ...extra,
});

const SECTIONS = [
  { title: 'My Insurance Policies', desc: 'View linked insurance policies and coverage details', route: '/patient/insurance', icon: 'shield', color: '#0891B2' },
  { title: 'Pre-authorization', desc: 'Submit and track pre-auth requests', route: '/patient/claims', icon: 'check', color: '#059669' },
  { title: 'Claims & Settlements', desc: 'File claims and track settlement status', route: '/patient/claims', icon: 'file', color: '#7C3AED' },
  { title: 'Insurance Documents', desc: 'Policy cards, claim forms, receipts', route: '/patient/documents', icon: 'doc', color: T.navy },
];

function SectionIcon({ type, color }: { type: string; color: string }) {
  const props = { width: 22, height: 22, viewBox: '0 0 24 24', fill: 'none', stroke: color, strokeWidth: 2, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
  if (type === 'shield') return <svg {...props}><path d="M12 2l8 4v6c0 5-3.5 9.7-8 11-4.5-1.3-8-6-8-11V6l8-4z" /><path d="M9 12l2 2 4-4" /></svg>;
  if (type === 'check') return <svg {...props}><rect x="3" y="3" width="18" height="18" rx="3" /><path d="M9 12l2 2 4-4" /></svg>;
  if (type === 'file') return <svg {...props}><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><path d="M14 2v6h6" /><path d="M9 15h6" /><path d="M9 11h6" /></svg>;
  return <svg {...props}><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><path d="M14 2v6h6" /></svg>;
}

function StatusBadge({ status }: { status: string }) {
  const s = status?.toLowerCase() || 'submitted';
  const colors: Record<string, { bg: string; fg: string; border: string }> = {
    submitted: { bg: '#FFF7ED', fg: '#EA580C', border: '#FED7AA' },
    under_review: { bg: '#EFF6FF', fg: '#2563EB', border: '#BFDBFE' },
    approved: { bg: '#ECFDF5', fg: '#059669', border: '#A7F3D0' },
    settled: { bg: '#ECFDF5', fg: '#059669', border: '#A7F3D0' },
    rejected: { bg: '#FEF2F2', fg: '#DC2626', border: '#FCA5A5' },
    processing: { bg: '#EFF6FF', fg: '#2563EB', border: '#BFDBFE' },
  };
  const c = colors[s] || colors.submitted;
  return (
    <span style={{
      fontFamily: T.font, fontSize: 10, fontWeight: 700,
      color: c.fg, background: c.bg, border: `1px solid ${c.border}`,
      borderRadius: 20, padding: '2px 8px', textTransform: 'capitalize',
    }}>
      {(status || 'Submitted').replace(/_/g, ' ').toLowerCase()}
    </span>
  );
}

const dayLabel = (iso?: string) => {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

/** One display shape for both the server's claims and the local mirror. */
interface ClaimRow {
  id: string;
  reference: string;
  type: string;
  policyNumber: string;
  amount: number;
  status: string;
  date: string;
  detail?: string;
  hospitalName?: string;
  settledAmount?: number;
  rejectionReason?: string;
  insurerReference?: string;
}

const fromApiClaim = (c: InsuranceClaim): ClaimRow => ({
  id: c.id,
  // The server's claim number is the only reference an insurer can look up.
  reference: c.claimNumber,
  type: c.type,
  policyNumber: c.policyNumber,
  amount: c.amount,
  status: c.status,
  date: c.claimDate,
  detail: c.serviceName || c.description,
  hospitalName: c.hospitalName,
  settledAmount: c.settledAmount,
  rejectionReason: c.rejectionReason,
  insurerReference: c.insurerReference,
});

/** A claim this device filed while the server list could not be loaded. */
const fromLocalClaim = (c: any): ClaimRow => ({
  id: c.id,
  reference: c.claimNumber || '',
  type: c.type || 'Claim',
  policyNumber: c.policyNumber || '',
  amount: c.amount || 0,
  status: c.status || 'SUBMITTED',
  date: c.claimDate || c.createdAt || '',
  detail: c.serviceType || c.description,
  hospitalName: c.hospital,
});

export default function InsurancePage() {
  const navigate = useNavigate();
  const localClaims = useStore((s: any) => s.insuranceClaims);
  const [apiClaims, setApiClaims] = useState<InsuranceClaim[]>([]);
  // The patient's own policies. A claim is filed against one of these — the
  // server checks the policy number belongs to the caller and refuses anything
  // else, so a free-text box here could only ever produce a rejection.
  const [policies, setPolicies] = useState<InsurancePolicy[]>([]);
  const [policiesLoaded, setPoliciesLoaded] = useState(false);
  // Real Health Vault documents — selectable as claim attachments.
  const [vaultDocs, setVaultDocs] = useState<{ id: string; title: string; category: string }[]>([]);
  const [selectedDocs, setSelectedDocs] = useState<string[]>([]);

  useEffect(() => {
    let alive = true;
    patientApi.getHealthVault()
      .then((items) => {
        if (!alive) return;
        setVaultDocs(items.map(i => ({
          // A document with no title is still a real file — name it by its
          // file rather than printing its database id at the patient.
          id: i.id,
          title: i.title?.trim() || i.fileName?.trim() || 'Untitled document',
          category: i.category,
        })));
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    let alive = true;
    patientApi.getInsurancePolicies()
      .then((rows) => { if (alive) { setPolicies(rows); setPoliciesLoaded(true); } })
      // A failed load is not "no policies" — the form stays hidden rather than
      // inviting a claim that cannot be filed.
      .catch(() => { if (alive) setPoliciesLoaded(true); });
    return () => { alive = false; };
  }, []);

  // Server claims are the source of truth; local mirrors (keyed by server id)
  // dedupe against them so nothing shows twice.
  useEffect(() => {
    let alive = true;
    patientApi.getInsuranceClaims()
      .then((rows) => { if (alive) setApiClaims(rows); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const claims: ClaimRow[] = useMemo(() => {
    const seen = new Set(apiClaims.map((c) => c.id));
    const mirrors: any[] = localClaims ?? [];
    return [
      ...apiClaims.map(fromApiClaim),
      ...mirrors.filter((c) => !seen.has(c.id)).map(fromLocalClaim),
    ];
  }, [apiClaims, localClaims]);

  // Only a live policy can be claimed against — the server refuses an expired
  // one, so the form does not offer it.
  const claimablePolicies = useMemo(
    () => policies.filter((p) => !p.expired && p.policyNumber),
    [policies],
  );

  // Claim form state
  const [claimType, setClaimType] = useState<'Claim' | 'Preauth'>('Claim');
  const [policyNumber, setPolicyNumber] = useState('');
  const [hospital, setHospital] = useState('');
  const [serviceType, setServiceType] = useState('');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [submitResult, setSubmitResult] = useState<{ refId: string; type: string } | null>(null);

  // Preselect the only policy there is, so the commonest case needs no choice.
  useEffect(() => {
    if (!policyNumber && claimablePolicies.length > 0) {
      setPolicyNumber(claimablePolicies[0].policyNumber);
    }
  }, [claimablePolicies, policyNumber]);

  const selectedPolicy = claimablePolicies.find((p) => p.policyNumber === policyNumber);

  const handleSubmit = async () => {
    setFormError(null);
    if (!policyNumber.trim()) { setFormError('Choose the policy this is filed against.'); return; }
    if (!amount || isNaN(parseFloat(amount)) || parseFloat(amount) <= 0) { setFormError('Please enter a valid amount.'); return; }
    if (!description.trim()) { setFormError('Please provide a description.'); return; }
    setSubmitting(true);
    try {
      // Real submission — the reference shown to the user is the SERVER's
      // claim number, never a client-minted string no insurer can resolve.
      // The hospital and the service travel as their own fields; the schema
      // has both, so nothing is folded into prose nothing can read back.
      const created = await patientApi.createInsuranceClaim({
        policyNumber: policyNumber.trim(),
        type: claimType,
        amount: parseFloat(amount),
        description: description.trim(),
        serviceName: serviceType.trim() || undefined,
        hospitalName: hospital.trim() || undefined,
        // Referenced by "title (vault:id)" so the insurer-facing record names
        // real Health Vault documents, never an empty pretend attachment list.
        documents: selectedDocs.map((docId) => {
          const d = vaultDocs.find(v => v.id === docId);
          return d ? `${d.title} (vault:${d.id})` : docId;
        }),
      });
      onInsuranceClaimCreated({
        id: created.id,
        policyNumber: policyNumber.trim(),
        type: claimType,
        amount: parseFloat(amount),
        description: description.trim(),
        hospital: hospital.trim() || undefined,
        serviceType: serviceType.trim() || undefined,
        documents: created.documents,
      });
      setApiClaims((prev) => [created, ...prev]);
      setSubmitResult({ refId: created.claimNumber, type: created.type });
    } catch (e: any) {
      // The server's own words — "That policy is not one of yours", "That
      // policy expired on …" — say more than a generic failure ever could.
      setFormError(e?.message || 'Submission failed — the server could not be reached. Your claim was NOT filed; please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setSubmitResult(null);
    setHospital('');
    setServiceType('');
    setAmount('');
    setDescription('');
    setSelectedDocs([]);
    setFormError(null);
  };

  const authState = patientAuthFlow.getState();
  const nhcId = authState.nhcId || '';

  const labelStyle: React.CSSProperties = { fontFamily: T.font, fontSize: 12, fontWeight: 600, color: T.text, display: 'block', marginBottom: 6 };
  const inputStyle: React.CSSProperties = {
    width: '100%', fontFamily: T.font, fontSize: 13, color: T.text, background: T.white,
    border: `1px solid ${T.border}`, borderRadius: 10, padding: '10px 12px', outline: 'none', boxSizing: 'border-box',
  };

  return (
    <div style={{ padding: 16, fontFamily: T.font, maxWidth: 1080, margin: '0 auto', paddingBottom: 100 }}>
      {/* Back nav */}
      <button onClick={() => navigate('/patient/care-services')} style={{
        display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none',
        fontFamily: T.font, fontSize: 12, fontWeight: 600, color: T.textSec, cursor: 'pointer',
        padding: '0 0 12px', margin: 0,
      }}>
        <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke={T.textSec} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
        Care Services
      </button>

      {/* Hero */}
      <div style={{ textAlign: 'center', marginBottom: 20 }}>
        <div style={{ width: 56, height: 56, borderRadius: 16, background: '#0891B212', display: 'grid', placeItems: 'center', margin: '0 auto 12px' }}>
          <svg width={28} height={28} viewBox="0 0 24 24" fill="none" stroke="#0891B2" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 2l8 4v6c0 5-3.5 9.7-8 11-4.5-1.3-8-6-8-11V6l8-4z" /><path d="M9 12l2 2 4-4" />
          </svg>
        </div>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: T.text, margin: 0 }}>Insurance & Claims</h2>
        <p style={{ fontSize: 12, color: T.textSec, marginTop: 4 }}>Policy management, pre-auth, and claim support</p>
        {nhcId && (
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#0891B208', border: '1px solid #0891B220', borderRadius: 20, padding: '3px 12px', marginTop: 8 }}>
            <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="#0891B2" strokeWidth="2"><path d="M12 2l8 4v6c0 5-3.5 9.7-8 11-4.5-1.3-8-6-8-11V6l8-4z" /></svg>
            <span style={{ fontSize: 10, fontWeight: 700, color: '#0891B2', letterSpacing: '0.02em' }}>{nhcId}</span>
          </div>
        )}
      </div>

      {/* Info banner */}
      <div style={{ ...card({ marginBottom: 16, background: '#0891B208', border: '1px solid #0891B220' }) }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="#0891B2" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10" /><path d="M12 8v4" /><path d="M12 16h.01" /></svg>
          <span style={{ fontSize: 12, fontWeight: 600, color: '#0891B2' }}>Your policies</span>
        </div>
        <p style={{ fontSize: 11, color: T.textSec, margin: 0, lineHeight: 1.5 }}>
          Keep your policy on your NeoHealthCard account so its number, cover and dates are to hand, and file claims and pre-auth requests against it.
        </p>
      </div>

      {/* Navigation sections */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 24 }}>
        {SECTIONS.map(s => (
          <button key={s.title} onClick={() => navigate(s.route)} style={{ ...card(), display: 'flex', alignItems: 'center', gap: 14, cursor: 'pointer', width: '100%', textAlign: 'left' }}>
            <div style={{ width: 44, height: 44, borderRadius: 12, background: `${s.color}12`, display: 'grid', placeItems: 'center', flexShrink: 0 }}>
              <SectionIcon type={s.icon} color={s.color} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: T.text }}>{s.title}</div>
              <div style={{ fontSize: 11, color: T.textTer, marginTop: 2 }}>{s.desc}</div>
            </div>
            <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke={T.textTer} strokeWidth="2"><path d="M9 18l6-6-6-6" /></svg>
          </button>
        ))}
      </div>

      {/* Scope notice — honest about what filing here does and doesn't do */}
      <div style={{ ...card({ marginBottom: 16, background: '#FFFBEB', border: '1px solid #FDE68A' }) }}>
        <p style={{ fontSize: 10, color: '#92400E', margin: 0, lineHeight: 1.5 }}>
          Claims filed here are recorded on your NeoHealthCard account. There is no direct insurer integration yet — share the claim record with your insurer/TPA as supporting documentation. NeoHealthCard does not guarantee claim approval.
        </p>
      </div>

      {/* Start New Claim / Preauth Form */}
      <div style={{ marginBottom: 24 }}>
        <h3 style={{ fontSize: 15, fontWeight: 800, color: T.text, margin: '0 0 12px' }}>Start New Claim / Preauth</h3>

        {submitResult ? (
          /* Success confirmation */
          <div style={card({ textAlign: 'center', padding: '24px 20px' })}>
            <div style={{ width: 56, height: 56, borderRadius: '50%', background: '#ECFDF5', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
              <svg width={24} height={24} viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
            </div>
            <h4 style={{ fontFamily: T.font, fontSize: 16, fontWeight: 800, color: T.text, margin: '0 0 6px' }}>
              {submitResult.type === 'Preauth' ? 'Pre-authorization' : 'Claim'} Recorded
            </h4>
            <p style={{ fontFamily: T.font, fontSize: 12, color: T.textSec, margin: '0 0 6px' }}>
              Your {submitResult.type === 'Preauth' ? 'pre-authorization request' : 'claim'} has been recorded on your NeoHealthCard account.
            </p>
            <p style={{ fontFamily: T.font, fontSize: 10, color: '#92400E', margin: '0 0 12px', lineHeight: 1.5 }}>
              Share this reference with your insurer/TPA — approval and settlement are decided by them.
            </p>
            <div style={{ background: '#0891B208', border: '1px solid #0891B220', borderRadius: 10, padding: '12px 16px', marginBottom: 12 }}>
              <p style={{ fontFamily: T.font, fontSize: 10, color: T.textSec, margin: '0 0 4px' }}>
                {submitResult.type === 'Preauth' ? 'Pre-auth reference' : 'Claim reference'}
              </p>
              <p style={{ fontFamily: T.font, fontSize: 16, fontWeight: 800, color: '#0891B2', margin: 0, letterSpacing: '0.03em' }}>{submitResult.refId}</p>
              <p style={{ fontFamily: T.font, fontSize: 11, color: T.textSec, margin: '6px 0 0' }}>Status: <StatusBadge status="Submitted" /></p>
            </div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <button onClick={() => navigate('/patient/claims')} style={{
                flex: 1, fontFamily: T.font, fontSize: 13, fontWeight: 700, color: T.white, background: '#0891B2',
                border: 'none', borderRadius: 12, padding: '12px 0', cursor: 'pointer',
              }}>View Claims</button>
              <button onClick={() => navigate('/patient/timeline')} style={{
                flex: 1, fontFamily: T.font, fontSize: 13, fontWeight: 700, color: '#0891B2', background: T.white,
                border: '1.5px solid #0891B2', borderRadius: 12, padding: '12px 0', cursor: 'pointer',
              }}>Go to Timeline</button>
            </div>
            <button onClick={resetForm} style={{
              width: '100%', fontFamily: T.font, fontSize: 12, fontWeight: 600, color: T.textSec,
              background: 'transparent', border: 'none', padding: '8px 0', cursor: 'pointer',
            }}>Submit Another</button>
            <button onClick={() => navigate('/patient/care-services')} style={{
              width: '100%', fontFamily: T.font, fontSize: 12, fontWeight: 600, color: T.textTer,
              background: 'transparent', border: 'none', padding: '6px 0', cursor: 'pointer',
            }}>Back to Care Services</button>
          </div>
        ) : !policiesLoaded ? (
          /* The form is built around the patient's real policies, so it waits
             for them rather than flashing an empty policy picker. */
          <div style={card({ textAlign: 'center', padding: '24px 20px', color: T.textSec, fontSize: 12 })}>
            Loading your policies…
          </div>
        ) : claimablePolicies.length === 0 ? (
          /* No live policy — a claim can only be filed against one, so say so
             instead of offering a form the server is bound to refuse. */
          <div style={card()}>
            <p style={{ fontFamily: T.font, fontSize: 13, fontWeight: 700, color: T.text, margin: '0 0 6px' }}>
              {policies.length === 0 ? 'Add a policy first' : 'No policy is currently in cover'}
            </p>
            <p style={{ fontFamily: T.font, fontSize: 11, color: T.textSec, margin: '0 0 14px', lineHeight: 1.5 }}>
              {policies.length === 0
                ? 'A claim is filed against one of your policies. Add the policy from your insurance card and it will be ready to claim against.'
                : 'Every policy on your account has expired. Your insurer will not accept a claim against an expired policy.'}
            </p>
            <button onClick={() => navigate('/patient/insurance')} style={{
              fontFamily: T.font, fontSize: 13, fontWeight: 700, color: T.white, background: '#0891B2',
              border: 'none', borderRadius: 12, padding: '11px 20px', cursor: 'pointer',
            }}>
              {policies.length === 0 ? 'Add a policy' : 'Open My Policies'}
            </button>
          </div>
        ) : (
          /* Claim form */
          <div style={card()}>
            {/* Type toggle */}
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Type</label>
              <div style={{ display: 'flex', gap: 8 }}>
                {(['Claim', 'Preauth'] as const).map(t => (
                  <button key={t} onClick={() => setClaimType(t)} style={{
                    flex: 1, fontFamily: T.font, fontSize: 13, fontWeight: 600,
                    color: claimType === t ? T.white : T.textSec,
                    background: claimType === t ? '#0891B2' : T.white,
                    border: `1.5px solid ${claimType === t ? '#0891B2' : T.border}`,
                    borderRadius: 10, padding: '9px 0', cursor: 'pointer',
                    transition: 'background 0.15s, color 0.15s',
                  }}>{t}</button>
                ))}
              </div>
              <div style={{ fontSize: 10, color: T.textTer, marginTop: 5 }}>
                {claimType === 'Preauth'
                  ? 'A pre-authorization is asked before treatment.'
                  : 'A claim is for treatment already received.'}
              </div>
            </div>

            {/* Policy — chosen from the patient's own, never typed */}
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Policy *</label>
              <select
                value={policyNumber}
                onChange={(e) => setPolicyNumber(e.target.value)}
                style={{ ...inputStyle, appearance: 'auto' as any }}
              >
                {claimablePolicies.map((p) => (
                  <option key={p.id} value={p.policyNumber}>
                    {[p.providerName, p.planName].filter(Boolean).join(' · ')} — {p.policyNumber}
                  </option>
                ))}
              </select>
              {selectedPolicy && (
                <div style={{ fontSize: 10, color: T.textTer, marginTop: 5, lineHeight: 1.5 }}>
                  Sum insured ₹{formatPrice(selectedPolicy.coverageAmount || 0)}
                  {selectedPolicy.validTo ? ` · valid until ${dayLabel(selectedPolicy.validTo)}` : ''}
                  {selectedPolicy.source === 'INSURER_CONFIRMED' || selectedPolicy.verifiedAt
                    ? ' · confirmed by your insurer'
                    : ' · self-reported'}
                </div>
              )}
            </div>

            {/* Hospital */}
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Hospital / Provider</label>
              <input
                type="text"
                placeholder="e.g. Apollo Hospital, Sarita Vihar"
                value={hospital}
                onChange={(e) => setHospital(e.target.value)}
                style={inputStyle}
              />
            </div>

            {/* Service Type */}
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Service / Procedure</label>
              <input
                type="text"
                placeholder="e.g. Knee replacement surgery, MRI scan"
                value={serviceType}
                onChange={(e) => setServiceType(e.target.value)}
                style={inputStyle}
              />
            </div>

            {/* Amount */}
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Amount (INR) *</label>
              <input
                type="number"
                placeholder="e.g. 25000"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                style={inputStyle}
              />
              {selectedPolicy?.coverageAmount != null && parseFloat(amount) > selectedPolicy.coverageAmount && (
                <div style={{ fontSize: 10, color: '#92400E', marginTop: 5, lineHeight: 1.5 }}>
                  More than this policy's sum insured (₹{formatPrice(selectedPolicy.coverageAmount)}). You can still file it — your insurer decides what it pays.
                </div>
              )}
            </div>

            {/* Description */}
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Description *</label>
              <textarea
                rows={3}
                placeholder="Describe the treatment, diagnosis, or procedure..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                style={{ ...inputStyle, resize: 'vertical' }}
              />
            </div>

            {/* Supporting documents — real Health Vault items */}
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>
                Supporting Documents <span style={{ fontWeight: 400, color: T.textTer }}>(from your Health Vault)</span>
              </label>
              {vaultDocs.length === 0 ? (
                <div style={{
                  width: '100%', border: `2px dashed ${T.border}`, borderRadius: 10, padding: '14px 12px',
                  textAlign: 'center', boxSizing: 'border-box', background: '#F9FAFB',
                }}>
                  <div style={{ fontFamily: T.font, fontSize: 11, color: T.textTer }}>
                    No documents in your Health Vault yet — upload bills, prescriptions, or discharge summaries there first.
                  </div>
                  <button onClick={() => navigate('/patient/health-vault')} style={{
                    marginTop: 8, fontFamily: T.font, fontSize: 10, fontWeight: 700, color: '#0891B2',
                    background: 'transparent', border: '1px solid #0891B2', borderRadius: 8, padding: '5px 12px', cursor: 'pointer',
                  }}>Open Health Vault</button>
                </div>
              ) : (
                <div style={{ border: `1px solid ${T.border}`, borderRadius: 10, padding: '8px 12px', background: T.white }}>
                  {vaultDocs.map((d) => (
                    <label key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', cursor: 'pointer', borderBottom: `1px solid ${T.borderLight}` }}>
                      <input
                        type="checkbox"
                        checked={selectedDocs.includes(d.id)}
                        onChange={() => setSelectedDocs(prev => prev.includes(d.id) ? prev.filter(x => x !== d.id) : [...prev, d.id])}
                        style={{ accentColor: '#0891B2' }}
                      />
                      <span style={{ fontFamily: T.font, fontSize: 11, color: T.text }}>{d.title}</span>
                      <span style={{ fontFamily: T.font, fontSize: 9, color: T.textTer, marginLeft: 'auto' }}>{d.category}</span>
                    </label>
                  ))}
                  <div style={{ fontFamily: T.font, fontSize: 9, color: T.textTer, marginTop: 6 }}>
                    Selected documents are named on the claim record so your insurer can ask for them.
                  </div>
                </div>
              )}
            </div>

            {/* Error */}
            {formError && (
              <div style={{ background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: 10, padding: '10px 14px', marginBottom: 12 }}>
                <p style={{ fontFamily: T.font, fontSize: 12, color: '#B91C1C', margin: 0 }}>{formError}</p>
              </div>
            )}

            {/* Submit */}
            <button
              onClick={handleSubmit}
              disabled={submitting}
              style={{
                width: '100%', fontFamily: T.font, fontSize: 14, fontWeight: 700, color: T.white,
                background: submitting ? '#67E8F9' : '#0891B2', border: 'none', borderRadius: 12,
                padding: '13px 0', cursor: submitting ? 'not-allowed' : 'pointer',
                transition: 'background 0.15s',
              }}
            >
              {submitting ? 'Submitting...' : `Submit ${claimType}`}
            </button>
          </div>
        )}
      </div>

      {/* My Claims section */}
      {claims.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <h3 style={{ fontSize: 15, fontWeight: 800, color: T.text, margin: '0 0 12px' }}>My Claims</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {claims.slice(0, 3).map((claim) => (
              <div key={claim.id} style={card({ padding: '14px 16px' })}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <span style={{ fontFamily: T.font, fontSize: 13, fontWeight: 700, color: T.text }}>
                    {claim.type === 'Preauth' ? 'Pre-authorization' : 'Claim'}
                  </span>
                  <StatusBadge status={claim.status} />
                </div>
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'baseline' }}>
                  {claim.reference && (
                    <span style={{ fontFamily: T.font, fontSize: 11, fontWeight: 700, color: T.text, letterSpacing: '0.02em' }}>{claim.reference}</span>
                  )}
                  {claim.amount > 0 && <span style={{ fontFamily: T.font, fontSize: 11, fontWeight: 600, color: '#0891B2' }}>₹{formatPrice(claim.amount)}</span>}
                  {claim.date && <span style={{ fontFamily: T.font, fontSize: 11, color: T.textSec }}>{dayLabel(claim.date)}</span>}
                </div>
                {claim.detail && (
                  <p style={{ fontFamily: T.font, fontSize: 11, color: T.textTer, margin: '6px 0 0', lineHeight: 1.4 }}>
                    {claim.detail.length > 80 ? claim.detail.slice(0, 80) + '...' : claim.detail}
                  </p>
                )}
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 6 }}>
                  {claim.hospitalName && <span style={{ fontFamily: T.font, fontSize: 10, color: T.textTer }}>{claim.hospitalName}</span>}
                  {claim.policyNumber && <span style={{ fontFamily: T.font, fontSize: 10, color: T.textTer }}>Policy {claim.policyNumber}</span>}
                  {claim.insurerReference && <span style={{ fontFamily: T.font, fontSize: 10, color: T.textTer }}>Insurer ref {claim.insurerReference}</span>}
                </div>
                {claim.settledAmount != null && (
                  <p style={{ fontFamily: T.font, fontSize: 11, fontWeight: 600, color: '#059669', margin: '6px 0 0' }}>
                    Settled ₹{formatPrice(claim.settledAmount)}
                  </p>
                )}
                {claim.rejectionReason && (
                  <p style={{ fontFamily: T.font, fontSize: 11, color: '#B91C1C', margin: '6px 0 0', lineHeight: 1.4 }}>
                    Reason: {claim.rejectionReason}
                  </p>
                )}
              </div>
            ))}
          </div>
          {claims.length > 3 && (
            <button onClick={() => navigate('/patient/claims')} style={{
              marginTop: 10, width: '100%', fontFamily: T.font, fontSize: 12, fontWeight: 700, color: '#0891B2',
              background: T.white, border: '1.5px solid #0891B2', borderRadius: 12, padding: '10px 0', cursor: 'pointer',
            }}>See all {claims.length} claims</button>
          )}
        </div>
      )}

      {/* Compliance disclaimer */}
      <div style={{ padding: 14, background: '#0891B208', borderRadius: 12, border: '1px solid #0891B215' }}>
        <p style={{ fontSize: 10, color: T.textTer, lineHeight: 1.6, margin: 0 }}>
          Insurance approval is subject to insurer verification. NeoHealthCard stores and shares consented health/claim documents; it does not guarantee claim approval. Final approval, rejection, co-pay, and settlement are decided by the insurer and hospital. Pre-authorization is subject to insurer processing timelines.
        </p>
      </div>
    </div>
  );
}
